import type { TypeCmsVideoExternal } from '@/schema'
type TypeVimeoData = {
  type: 'video'
  version: string
  provider_name: 'Vimeo',
  provider_url: 'https://vimeo.com/'
  title: string
  author_name: string
  author_url: string
  is_plus?: string
  account_type: 'basic' | string
  html: string
  width: number
  height: number
  duration: number
  description?: string
  thumbnail_width?: number
  thumbnail_height?: number
  thumbnail_url_with_play_button?: string
  upload_date: Date
  video_id: number
  uri: string
}

export type TypeExtendedVideoData = TypeVimeoData & {
  aspectRatio?: number
  orientation?: 'portrait' | 'landscape' | 'square'
}

const platformJsonUrl = {
  vimeo: (id) => `https://vimeo.com/api/oembed.json?url=https://www.vimeo.com/${id}`,
  youtube: (id) => `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${id}&format=json`
}

export const getVideoData = async (id: TypeCmsVideoExternal['longVideo'] | TypeCmsVideoExternal['shortVideo'], platform: TypeCmsVideoExternal['platform']) => {
  const url = platformJsonUrl[platform](id)

  const response = await fetch(url)
  const dataRes = await response.json()
  return dataRes
}

/**
* Computes the greatest common divisor (GCD) to simplify the width and height.
*/
function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b)
}

export function calculateAspectRatio(width: number, height: number, format: 'colon' | 'slash' | 'decimal' = 'decimal'): string | number {
  const divisor = gcd(width, height)
  const aspectWidth = width / divisor
  const aspectHeight = height / divisor

  switch (format) {
    case 'colon':
      return `${aspectWidth} : ${aspectHeight}`
    case 'slash':
      return `${aspectWidth} / ${aspectHeight}`
    case 'decimal':
      const aspectRatioDecimal = aspectWidth / aspectHeight
      return parseFloat(aspectRatioDecimal.toFixed(2))
    default:
      throw new Error('Invalid format. Use "colon", "slash", or "decimal".')
  }
}

export function getOrientationFromAspectRatio(aspectRatio: number): 'portrait' | 'landscape' | 'square' {
  if (aspectRatio > 1) {
    return 'landscape'
  } else if (aspectRatio < 1) {
    return 'portrait'
  } else {
    return 'square'
  }
}
